// src/core/score/efhDealScore.ts
// Bruecke zwischen den EFH-Mechanik-Kennzahlen und dem analyzer-uebergreifenden
// Deal-Score (dealScore.ts). Gleiches Muster wie etwDealScore.ts/mfhDealScore.ts.
//
// EFH hat (anders als ETW/MFH) keine extrahierte core/metrics/*.ts-Datei -- die
// Kennzahlen werden bisher direkt inline in EinfamilienhausCheck.tsx berechnet
// (kein Flaechenfeld, rein €-basiert). Diese Datei extrahiert genau diese Rechnung
// einmal als reine Funktion (computeEfhMetrics), damit Analyzer-Seite und
// Snapshot/PDF-Pfad dieselbe Berechnung nutzen.
//
// Finanzierung seit 14.10.2026 Runde 2: Zins + anfaengliche Tilgung (flache
// Annuitaet), wie ETW/MFH -- vorher Zins + Laufzeit (exakt amortisierende
// Annuitaet), siehe DEAL-FLOW-STATUS.md "Als Naechstes nach Runde 1".

import { computeDealScore, type DealScoreResult, type LageInput } from "./dealScore.js";
import { EFH_SCORE_CONFIG, RENDITE_ZUSCHLAG_PP, BEWIRTSCHAFTUNG_PAUSCHALE_PCT } from "./dealScoreConfig.js";
import { resolveImportPcts, pctSum } from "../../lib/purchaseCosts.js";
import { computeFinancing, eigenkapitalFromLegacyLoan } from "../financing.js";
import { resolveBewirtschaftungModus } from "../bewirtschaftung.js";
import { EFH_INPUT_DEFAULTS, type EfhSnapshotInput } from "../../lib/efhSnapshot.js";

const STRESS_ZINS_DELTA = 0.01;
const STRESS_MIETE_FAKTOR = 0.9;

export type EfhMetricsInput = {
  kaufpreis: number;
  priceAdjPct: number;
  applyAdjustments: boolean;
  nkPct: number;
  jahreskaltmiete: number;
  rentAdjPct: number;
  mietausfallPct: number;
  nichtUmlagefaehigJahr: number;
  instandhaltungJahr: number;
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5, Korrektur der frueheren "3 % uebernehmen"-
  // Vorgabe): optional, siehe MfhMetricsInput (core/metrics/mfh.ts) fuer die ausfuehrliche
  // Begruendung -- identisches Muster. Legacy-Zweig (kein Modus): nichtUmlagefaehigJahr +
  // instandhaltungJahr, exakt wie bisher.
  bewirtschaftungModus?: "pauschal" | "einzeln";
  verwaltung?: number;
  instandhaltungJeM2Jahr?: number;
  flaeche?: number;
  // Stress-Pass-Fix (18.10.2026, Dominiks Vorgabe): siehe MfhMetricsInput.pauschaleBasisMiete
  // (core/metrics/mfh.ts) -- identisches Muster. Pauschal bleibt unter Mietstress fix.
  pauschaleBasisMiete?: number;
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026), wie MFH: Eigenkapital in Euro statt
   *  Beleihungsquote (LTV). */
  eigenkapital: number;
  zinsPct: number;
  tilgungPct: number;
};

export type EfhMetrics = {
  kaufpreisView: number;
  allIn: number;
  /** Jahresmiete nach Miet-Anpassung, aber VOR Mietausfall-Abzug -- Basis fuer Bruttorendite (analog grossRentYear bei ETW/MFH). */
  jahresmieteAdj: number;
  mieteEffektiv: number;
  laufendeKostenJahr: number;
  /** Fuer die Breakdown-Anzeige (Baustein 5): Aufteilung von laufendeKostenJahr auf die 3
   *  Einzeln-Felder (Pauschal/Legacy: nichtUmlagefaehigEff=Summe, Rest 0). */
  nichtUmlagefaehigEff: number;
  verwaltungEff: number;
  instandhaltungEff: number;
  noiJahr: number;
  loan: number;
  /** Bedarf (All-in) minus EK minus Kredit; 0, wenn das Eigenkapital die Nebenkosten deckt. */
  finanzierungsluecke: number;
  annuityYear: number;
  /** Phase-4 Runde 2b, Baustein 4 (Bankbericht aus der Akte): additiv, aendert noiJahr/
   *  Score nicht -- bisher nur lokal in EinfamilienhausCheck.tsx berechnet. */
  interestY1: number;
  principalY1: number;
};

export function computeEfhMetrics(input: EfhMetricsInput): EfhMetrics {
  const adjustedPrice = Math.round(input.kaufpreis * (1 + input.priceAdjPct));
  const kaufpreisView = input.applyAdjustments ? adjustedPrice : input.kaufpreis;
  const jahresmieteAdj = input.jahreskaltmiete * (1 + input.rentAdjPct);

  const mietausfall = jahresmieteAdj * input.mietausfallPct;
  const mieteEffektiv = jahresmieteAdj - mietausfall;

  // Bewirtschaftung Pauschal/Einzeln (Baustein 5) -- siehe Feldkommentar in EfhMetricsInput.
  let nichtUmlagefaehigEff: number;
  let verwaltungEff: number;
  let instandhaltungEff: number;
  if (input.bewirtschaftungModus === "pauschal") {
    nichtUmlagefaehigEff = (input.pauschaleBasisMiete ?? mieteEffektiv) * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.efh;
    verwaltungEff = 0;
    instandhaltungEff = 0;
  } else if (input.bewirtschaftungModus === "einzeln") {
    nichtUmlagefaehigEff = input.nichtUmlagefaehigJahr;
    verwaltungEff = Math.max(0, input.verwaltung ?? 0);
    instandhaltungEff = Math.max(0, input.instandhaltungJeM2Jahr ?? 0) * Math.max(0, input.flaeche ?? 0);
  } else {
    // Legacy (kein Modus, z.B. unveraenderter freier Einstieg): exakt wie bisher.
    nichtUmlagefaehigEff = input.nichtUmlagefaehigJahr;
    verwaltungEff = 0;
    instandhaltungEff = input.instandhaltungJahr;
  }
  const laufendeKostenJahr = nichtUmlagefaehigEff + verwaltungEff + instandhaltungEff;
  const noiJahr = mieteEffektiv - laufendeKostenJahr;

  const nkBetrag = Math.round(kaufpreisView * input.nkPct);
  const allIn = kaufpreisView + nkBetrag;

  // Finanzierung (Dominiks Vorgabe, 16.10.2026, einheitliche Darlehensregel -- wie MFH):
  // das EK deckt zuerst die Nebenkosten, der Kredit den Rest des Kaufpreises. Reicht das EK
  // nicht fuer die Nebenkosten, bleibt eine Luecke, statt stillschweigend einen zu grossen
  // Kredit anzunehmen (vorher: Kredit = Kaufpreis x ltvPct, finanzierte dadurch implizit
  // niemals NK, konnte aber umgekehrt auch NIE eine Luecke erzeugen). Gemeinsame Funktion
  // (core/financing.ts) statt einer eigenen Kopie der Formel -- dieselbe nutzen auch Gewerbe/
  // Gemischt/ETW (nach deren Umstellung).
  const financing = computeFinancing({ kaufpreis: kaufpreisView, nebenkosten: nkBetrag, eigenkapital: input.eigenkapital, financingOn: input.financingOn });
  const loan = financing.loan;
  const finanzierungsluecke = financing.finanzierungsluecke;
  // Flache Zins+Tilgung-Annuitaet (wie MFH/ETW/Gemischt), seit 14.10.2026 Runde 2 --
  // vorher eine exakt amortisierende Zins+Laufzeit-Annuitaet (annuityExact()), siehe
  // DEAL-FLOW-STATUS.md "Als Naechstes nach Runde 1".
  const annuityYear = input.financingOn ? loan * (input.zinsPct + input.tilgungPct) : 0;
  const interestY1 = input.financingOn ? loan * input.zinsPct : 0;
  const principalY1 = input.financingOn ? Math.max(0, annuityYear - interestY1) : 0;

  return {
    kaufpreisView, allIn, jahresmieteAdj, mieteEffektiv, laufendeKostenJahr,
    nichtUmlagefaehigEff, verwaltungEff, instandhaltungEff,
    noiJahr, loan, finanzierungsluecke, annuityYear,
    interestY1, principalY1,
  };
}

/** Migration (16.10.2026, einheitliche Darlehensregel): ein alter EFH-Snapshot/-Export
 *  (vor der Umstellung von ltvPct auf eigenkapital) speicherte Kredit = Kaufpreis x ltvPct,
 *  ohne NK-Deckel. Aequivalentes Eigenkapital ueber die gemeinsame Migrations-Funktion
 *  (core/financing.ts), die auch den (bei EFH praktisch nie vorkommenden) Randfall
 *  Kredit_alt > Kaufpreis korrekt behandelt. */
export function efhEigenkapitalFromLegacyLtv(kaufpreis: number, ltvPct: number, nkPct: number): number {
  const nkBetrag = Math.round(kaufpreis * nkPct);
  const kreditAlt = kaufpreis * ltvPct;
  return eigenkapitalFromLegacyLoan(kaufpreis + nkBetrag, kreditAlt);
}

/** Loest das Eigenkapital aus einem (ggf. alten) EFH-Snapshot-Input auf -- wendet
 *  efhEigenkapitalFromLegacyLtv() an, wenn legacyLtvPct gesetzt ist, sonst der direkte Wert. */
export function resolveEfhEigenkapital(
  rawInput: { kaufpreis: number; eigenkapital: number; legacyLtvPct: number | null },
  nkPct: number
): number {
  return rawInput.legacyLtvPct == null
    ? rawInput.eigenkapital
    : efhEigenkapitalFromLegacyLtv(rawInput.kaufpreis, rawInput.legacyLtvPct, nkPct);
}

/**
 * Migrations-Bruecke Bewirtschaftung Pauschal/Einzeln (Baustein 5), NACHKORRIGIERT
 * 17.10.2026 -- identisches Muster wie resolveMfhBewirtschaftung() (core/score/
 * mfhDealScore.ts). Siehe core/bewirtschaftung.ts' resolveBewirtschaftungModus() fuer die
 * Regel: "Pauschal" nur, wenn die gespeicherten nicht-umlagefaehigen Kosten der Pauschale
 * (auf effektive Miete) exakt entsprechen (Toleranz 1 €) UND Instandhaltung 0 ist -- sonst
 * "Einzeln" mit den gespeicherten Werten 1:1 (Score bleibt in jedem Fall unveraendert).
 */
/** flaecheM2 existierte vor diesem Feature nicht -- ein alter Snapshot/eine alte Zeile hat
 *  dort 0, nicht die UI-Default-Flaeche. Zentrale Stelle fuer den Fallback (typisches EFH,
 *  140 m²), damit Migrationsumrechnung und laufende NOI-Berechnung IMMER dieselbe Flaeche
 *  verwenden -- sonst ginge der historische €-Betrag beim Umrechnen in €/m²/Jahr verloren
 *  (Umrechnung mit Fallback-Flaeche, NOI-Berechnung mit 0 ergaebe wieder 0). */
export function efhFlaecheFromRawInput(rawInput: { flaecheM2?: number }): number {
  return rawInput.flaecheM2 && rawInput.flaecheM2 > 0 ? rawInput.flaecheM2 : EFH_INPUT_DEFAULTS.flaecheM2;
}

export function resolveEfhBewirtschaftung(
  rawInput: { bewirtschaftungModus?: unknown; instandhaltungJahr: number; nichtUmlagefaehigJahr: number; mietausfallPct: number; jahreskaltmiete: number; verwaltung?: number; instandhaltungJeM2Jahr?: number },
  flaeche: number
): { bewirtschaftungModus: "pauschal" | "einzeln"; verwaltung: number; instandhaltungJeM2Jahr: number; migriertVonAlt: boolean; instandhaltungVermutlichEnthalten: boolean } {
  // Naeherung an mieteEffektiv (die tatsaechliche Bemessungsgrundlage der Pauschale, siehe
  // computeEfhMetrics) -- rentAdjPct bleibt hier unberuecksichtigt (bei einem frisch
  // geladenen Bestandsobjekt neutral), macht die Erkennung im Zweifel vorsichtiger, nie
  // score-riskanter (siehe resolveMfhBewirtschaftung()-Kommentar).
  const mieteEffektivApprox = rawInput.jahreskaltmiete * (1 - Math.max(0, Math.min(1, rawInput.mietausfallPct)));
  const pauschaleBetrag = mieteEffektivApprox * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.efh;
  // erlaubePauschal=false (Live-Befund 17.10.2026, nach dem Push, MFH-Analogon -- gleicher
  // Fehler hier): EFHs Legacy-Modell behandelt nicht-umlagefaehige Kosten als FIXEN €-Betrag,
  // skaliert unter Stress NICHT mit der gestressten Miete -- "Pauschal" skaliert IMMER mit,
  // selbst wenn der Basisbetrag exakt passt. Siehe core/bewirtschaftung.ts' Dateikommentar.
  const { modus, migriertVonAlt } = resolveBewirtschaftungModus(
    rawInput.bewirtschaftungModus, rawInput.instandhaltungJahr, rawInput.nichtUmlagefaehigJahr, pauschaleBetrag, false
  );
  if (modus === "pauschal") {
    return { bewirtschaftungModus: "pauschal", verwaltung: 0, instandhaltungJeM2Jahr: 0, migriertVonAlt: false, instandhaltungVermutlichEnthalten: false };
  }
  let verwaltung: number, instandhaltungJeM2Jahr: number;
  if (migriertVonAlt) {
    verwaltung = 0;
    instandhaltungJeM2Jahr = flaeche > 0 ? rawInput.instandhaltungJahr / flaeche : 0;
  } else {
    verwaltung = rawInput.verwaltung ?? 0;
    instandhaltungJeM2Jahr = rawInput.instandhaltungJeM2Jahr ?? 0;
  }
  const instandhaltungVermutlichEnthalten = instandhaltungJeM2Jahr === 0 && rawInput.nichtUmlagefaehigJahr >= pauschaleBetrag;
  return { bewirtschaftungModus: "einzeln", verwaltung, instandhaltungJeM2Jahr, migriertVonAlt, instandhaltungVermutlichEnthalten };
}

function toDealScoreInput(
  metrics: EfhMetrics,
  stressMetrics: EfhMetrics,
  financingOn: boolean,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein"
) {
  return {
    kaufpreis: metrics.kaufpreisView,
    gesamtinvestition: metrics.allIn,
    jahreskaltmiete: metrics.jahresmieteAdj,
    noiJahr: metrics.noiJahr,
    annuitaetJahr: metrics.annuityYear,
    financingOn,
    stressNoiJahr: stressMetrics.noiJahr,
    stressAnnuitaetJahr: stressMetrics.annuityYear,
    lage,
    renditeZuschlagPp: RENDITE_ZUSCHLAG_PP.efh,
    vermietungsstatus,
  };
}

/** Fuer die lebende Analyzer-Seite: metrics ist das Ergebnis eines bereits erfolgten computeEfhMetrics()-Aufrufs. */
export function computeEfhDealScoreFromMetrics(
  baseInput: EfhMetricsInput,
  metrics: EfhMetrics,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const stressInput: EfhMetricsInput = {
    ...baseInput,
    zinsPct: baseInput.zinsPct + STRESS_ZINS_DELTA,
    jahreskaltmiete: baseInput.jahreskaltmiete * STRESS_MIETE_FAKTOR,
    // Stress-Pass-Fix (18.10.2026): Pauschal auf Basis-Miete fix, siehe mfhDealScore.ts.
    pauschaleBasisMiete: metrics.mieteEffektiv,
  };
  const stressMetrics = computeEfhMetrics(stressInput);
  return computeDealScore(
    toDealScoreInput(metrics, stressMetrics, baseInput.financingOn, lage, vermietungsstatus),
    EFH_SCORE_CONFIG
  );
}

/** Fuer "Meine Deals"/PDF: nur die gespeicherten Rohdaten vorhanden, kein live computeEfhMetrics()-Ergebnis. */
export function computeEfhDealScoreFromSnapshotInput(
  rawInput: EfhSnapshotInput & Record<string, unknown>,
  playground: { priceAdjPct: number; rentAdjPct: number; applyAdjustments: boolean },
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const nkPct = pctSum(resolveImportPcts(rawInput, "efh"));
  const flaeche = efhFlaecheFromRawInput(rawInput);
  const bewirtschaftung = resolveEfhBewirtschaftung(rawInput, flaeche);
  const base: EfhMetricsInput = {
    kaufpreis: rawInput.kaufpreis,
    priceAdjPct: playground.priceAdjPct,
    applyAdjustments: playground.applyAdjustments,
    nkPct,
    jahreskaltmiete: rawInput.jahreskaltmiete,
    rentAdjPct: playground.rentAdjPct,
    mietausfallPct: rawInput.mietausfallPct,
    nichtUmlagefaehigJahr: rawInput.nichtUmlagefaehigJahr,
    instandhaltungJahr: rawInput.instandhaltungJahr,
    bewirtschaftungModus: bewirtschaftung.bewirtschaftungModus, verwaltung: bewirtschaftung.verwaltung,
    instandhaltungJeM2Jahr: bewirtschaftung.instandhaltungJeM2Jahr, flaeche,
    financingOn: rawInput.financingOn,
    eigenkapital: resolveEfhEigenkapital(rawInput, nkPct),
    zinsPct: rawInput.zinsPct,
    tilgungPct: rawInput.tilgungPct,
  };
  const metrics = computeEfhMetrics(base);
  return computeEfhDealScoreFromMetrics(base, metrics, lage, vermietungsstatus);
}
